/// <amd-module name="Build/Common/Library/Indicator" />
import { BuildResult, BuildStatus, TaskResult, TimelineRecordState } from "Build/Client/Contracts/Build";
import * as Resources from "Build/Common/Library/Resources";

import { IStatusProps, Statuses } from "VSSUI/Status";
// https://azdevinternal.azureedge.net/components/status
export namespace Status {
    export const Cancelled = "cancelled";
    export const Failed = "failed";
    export const InProgress = "inprogress";
    /**
     * Equivalent to "Not started" should map to Status's "Queued", usually for "dependencies not satisfied"
     */
    export const Pending = "pending";
    /**
     * Maps to Status's "Waiting", usually for "all dependencies are satisfied"
     */
    export const Queued = "queued";
    export const Skipped = "skipped";
    export const Succeeded = "succeeded";
    export const Unknown = "unknown";
    /**
     * Maps to Status's "Waiting", usually for "checkpoint not satisfied"
     */
    export const Waiting = "waiting";
    export const Warning = "warning";
}

export interface IStatusIndicatorData {
    statusProps: IStatusProps;
    type: string;
    label: string;
}

export function getStatusIndicatorData(status: string): IStatusIndicatorData {
    status = status || "";
    status = status.toLowerCase();
    const indicatorData: IStatusIndicatorData = {
        statusProps: Statuses.Queued,
        type: Status.Queued,
        label: Resources.StatusUnknown
    };
    switch (status) {
        case Status.Cancelled:
            indicatorData.statusProps = Statuses.Canceled;
            indicatorData.type = Status.Cancelled;
            indicatorData.label = Resources.StatusCanceled;
            break;
        case Status.Failed:
            indicatorData.statusProps = Statuses.Failed;
            indicatorData.type = Status.Failed;
            indicatorData.label = Resources.StatusFailed;
            break;
        case Status.InProgress:
            indicatorData.statusProps = Statuses.Running;
            indicatorData.type = Status.InProgress;
            indicatorData.label = Resources.StatusRunning;
            break;
        case Status.Pending:
            indicatorData.statusProps = Statuses.Queued;
            indicatorData.type = Status.Pending;
            indicatorData.label = Resources.StatusWaiting;
            break;
        case Status.Queued:
            indicatorData.statusProps = Statuses.Waiting;
            indicatorData.type = Status.Waiting;
            indicatorData.label = Resources.StatusQueued;
            break;
        case Status.Skipped:
            indicatorData.statusProps = Statuses.Skipped;
            indicatorData.type = Status.Skipped;
            indicatorData.label = Resources.StatusSkipped;
            break;
        case Status.Succeeded:
            indicatorData.statusProps = Statuses.Success;
            indicatorData.type = Status.Succeeded;
            indicatorData.label = Resources.StatusSuccess;
            break;
        case Status.Waiting:
            indicatorData.statusProps = Statuses.Waiting;
            indicatorData.type = Status.Waiting;
            indicatorData.label = Resources.StatusWaiting;
            break;
        case Status.Warning:
            indicatorData.statusProps = Statuses.Warning;
            indicatorData.type = Status.Warning;
            indicatorData.label = Resources.StatusWarning;
            break;
    }

    return indicatorData;
}

/**
 * Gets an IStatusIndicatorData suitable for use with the Status component based on a build status and result
 * @param status The build status
 * @param result The build result
 * @returns An IStatusIndicatorData matching the BuildStatus and BuildResult
 */
export function getBuildStatusIndicatorData(status: BuildStatus, result?: BuildResult): IStatusIndicatorData {
    let statusType = Status.Unknown;

    switch (status) {
        case BuildStatus.Cancelling:
            statusType = Status.Cancelled;
            break;
        case BuildStatus.InProgress:
            statusType = Status.InProgress;
            break;
        case BuildStatus.NotStarted:
        case BuildStatus.Postponed:
            statusType = Status.Queued;
            break;
        case BuildStatus.Completed:
            switch (result) {
                case BuildResult.Succeeded:
                    statusType = Status.Succeeded;
                    break;
                case BuildResult.PartiallySucceeded:
                    statusType = Status.Warning;
                    break;
                case BuildResult.Failed:
                    statusType = Status.Failed;
                    break;
                case BuildResult.Canceled:
                    statusType = Status.Cancelled;
                    break;
            }
    }

    return getStatusIndicatorData(statusType);
}

/**
 * Gets an IStatusIndicatorData suitable for use with the Status component based on a timeline record state and result
 * @param state The timeline record status
 * @param result The timeline record result
 * @param isWaiting Send true if record is waiting on something, for example - if checks are not yet satisfied
 * @param readyForQueue Send true if record ready to go into the queue, for example - all dependencies are satisfied. Note that default TimelineRecordState.Pending implies that dependencies are not satisfied yet.
 * @returns An IStatusIndicatorData matching the TimelineRecordState and TaskResult
 */
export function getTimelineStatusIndicatorData(state: TimelineRecordState, result?: TaskResult, isWaiting?: boolean, readyForQueue?: boolean): IStatusIndicatorData {
    let statusType = Status.Unknown;

    switch (state) {
        case TimelineRecordState.Pending:
            if (!!isWaiting) {
                statusType = Status.Waiting; // Eg: checks not satisfied
            }
            else if (!!readyForQueue) {
                statusType = Status.Queued;// Eg: dependencies satisfied
            }
            else {
                statusType = Status.Pending;// Eg: default, dependencies not satisfied
            }
            break;
        case TimelineRecordState.InProgress:
            statusType = Status.InProgress;
            break;
        case TimelineRecordState.Completed:
            switch (result) {
                case TaskResult.Succeeded:
                    statusType = Status.Succeeded;
                    break;
                case TaskResult.SucceededWithIssues:
                    statusType = Status.Warning;
                    break;
                case TaskResult.Failed:
                    statusType = Status.Failed;
                    break;
                case TaskResult.Canceled:
                    statusType = Status.Cancelled;
                    break;
                case TaskResult.Skipped:
                    statusType = Status.Skipped;
                    break;
                case TaskResult.Abandoned:
                    statusType = Status.Cancelled;
                    break;
            }
    }

    return getStatusIndicatorData(statusType);
}
